// frontend/src/modules/invoicing/api.ts
import { api } from '../../core/api-client';
import { downloadFile } from '../payroll/api';
import type {
  Client, CreateClientInput,
  Service, CreateServiceInput,
  Invoice, InvoiceWithItems, CreateInvoiceInput, CreateInvoiceItemInput,
  RecurringInvoice, CreateRecurringInput,
  InvoiceStatus, DocumentType, InvoicePayment, TaxProfile,
} from './types';

// ---- Clients ----
export const clientsApi = {
  list: () => api<{ clients: Client[] }>('/api/invoicing/clients').then((r) => r.clients),
  get: (id: string) => api<{ client: Client }>(`/api/invoicing/clients/${id}`).then((r) => r.client),
  create: (input: CreateClientInput) =>
    api<{ client: Client }>('/api/invoicing/clients', { method: 'POST', body: input }).then((r) => r.client),
  update: (id: string, input: Partial<CreateClientInput> & { status?: 'active' | 'archived' }) =>
    api<{ client: Client }>(`/api/invoicing/clients/${id}`, { method: 'PATCH', body: input }).then((r) => r.client),
  remove: (id: string) =>
    api<void>(`/api/invoicing/clients/${id}`, { method: 'DELETE' }),
  importRows: (rows: Record<string, string>[]) =>
    api<ImportSummary>('/api/invoicing/clients/import', { method: 'POST', body: { rows } }),
};

export interface ImportSummary {
  created: number;
  skipped: { row: number; reason: string }[];
}

// ---- Services ----
export const servicesApi = {
  list: () => api<{ services: Service[] }>('/api/invoicing/services').then((r) => r.services),
  get: (id: string) => api<{ service: Service }>(`/api/invoicing/services/${id}`).then((r) => r.service),
  create: (input: CreateServiceInput) =>
    api<{ service: Service }>('/api/invoicing/services', { method: 'POST', body: input }).then((r) => r.service),
  update: (id: string, input: Partial<CreateServiceInput> & { is_active?: boolean }) =>
    api<{ service: Service }>(`/api/invoicing/services/${id}`, { method: 'PATCH', body: input }).then((r) => r.service),
  remove: (id: string) =>
    api<void>(`/api/invoicing/services/${id}`, { method: 'DELETE' }),
};

// ---- Invoices ----
export interface InvoiceListParams {
  status?: InvoiceStatus;
  client_id?: string;
  document_type?: DocumentType;
  limit?: number;
}

export const invoicesApi = {
  list: (params: InvoiceListParams = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.client_id) qs.set('client_id', params.client_id);
    if (params.document_type) qs.set('document_type', params.document_type);
    if (params.limit) qs.set('limit', String(params.limit));
    const tail = qs.toString();
    return api<{ invoices: Invoice[] }>(`/api/invoicing/invoices${tail ? `?${tail}` : ''}`).then((r) => r.invoices);
  },
  get: (id: string) =>
    api<{ invoice: InvoiceWithItems }>(`/api/invoicing/invoices/${id}`).then((r) => r.invoice),
  create: (input: CreateInvoiceInput) =>
    api<{ invoice: InvoiceWithItems }>('/api/invoicing/invoices', { method: 'POST', body: input }).then((r) => r.invoice),
  update: (id: string, input: Partial<CreateInvoiceInput>) =>
    api<{ invoice: InvoiceWithItems }>(`/api/invoicing/invoices/${id}`, { method: 'PATCH', body: input }).then((r) => r.invoice),
  send: (id: string) =>
    api<{ invoice: Invoice }>(`/api/invoicing/invoices/${id}/send`, { method: 'POST', body: {} }).then((r) => r.invoice),
  markPaid: (id: string) =>
    api<{ invoice: Invoice }>(`/api/invoicing/invoices/${id}/mark-paid`, { method: 'POST', body: {} }).then((r) => r.invoice),
  cancel: (id: string) =>
    api<{ invoice: Invoice }>(`/api/invoicing/invoices/${id}/cancel`, { method: 'POST', body: {} }).then((r) => r.invoice),
  pdfUrl: (id: string) => `/api/invoicing/invoices/${id}/pdf`,

  /** QuickBooks/Xero-ready CSV export, honoring an optional issued-date range. */
  downloadExportCsv: (range: { from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return downloadFile(`/api/invoicing/invoices/export${suffix}`, `invoices-${new Date().toISOString().slice(0, 10)}.csv`);
  },

  // ── Partial payments (036) ──
  recordPayment: (id: string, input: { amount: number; paid_at?: string | null; method?: string | null; note?: string | null }) =>
    api<{ invoice: InvoiceWithItems }>(`/api/invoicing/invoices/${id}/payments`, { method: 'POST', body: input }).then((r) => r.invoice),
  listPayments: (id: string) =>
    api<{ payments: InvoicePayment[] }>(`/api/invoicing/invoices/${id}/payments`).then((r) => r.payments),
  /**
   * Undo a manually recorded payment. Gateway money must go back through a
   * refund, so those 409 with `provider_payment` — surface that message.
   * Returns the invoice with its recomputed balance + status.
   */
  deletePayment: (id: string, paymentId: string) =>
    api<{ invoice: InvoiceWithItems }>(`/api/invoicing/invoices/${id}/payments/${paymentId}`, { method: 'DELETE' }).then((r) => r.invoice),

  // ── Estimates (036) ──
  accept: (id: string) =>
    api<{ invoice: Invoice }>(`/api/invoicing/invoices/${id}/accept`, { method: 'POST', body: {} }).then((r) => r.invoice),
  decline: (id: string) =>
    api<{ invoice: Invoice }>(`/api/invoicing/invoices/${id}/decline`, { method: 'POST', body: {} }).then((r) => r.invoice),
  convert: (id: string) =>
    api<{ invoice: InvoiceWithItems }>(`/api/invoicing/invoices/${id}/convert`, { method: 'POST', body: {} }).then((r) => r.invoice),

  // ── Credit notes (036) ──
  createCreditNote: (id: string, input: { amount?: number; items?: CreateInvoiceItemInput[]; notes?: string | null }) =>
    api<{ credit_note: InvoiceWithItems; related_invoice: Invoice }>(`/api/invoicing/invoices/${id}/credit-note`, { method: 'POST', body: input }),

  bulkMarkPaid: (invoiceIds: string[]) =>
    api<BulkInvoiceResult>('/api/invoicing/invoices/bulk/mark-paid', { method: 'POST', body: { invoice_ids: invoiceIds } }),
  bulkSend: (invoiceIds: string[]) =>
    api<BulkInvoiceResult>('/api/invoicing/invoices/bulk/send', { method: 'POST', body: { invoice_ids: invoiceIds } }),
};

export interface BulkInvoiceResult {
  updated: number;
  skipped: Array<{ id: string; reason: string }>;
}

// ---- Recurring ----
export const recurringApi = {
  list: () =>
    api<{ recurring: RecurringInvoice[] }>('/api/invoicing/recurring').then((r) => r.recurring),
  get: (id: string) =>
    api<{ recurring: RecurringInvoice }>(`/api/invoicing/recurring/${id}`).then((r) => r.recurring),
  create: (input: CreateRecurringInput) =>
    api<{ recurring: RecurringInvoice }>('/api/invoicing/recurring', { method: 'POST', body: input }).then((r) => r.recurring),
  update: (id: string, input: Partial<CreateRecurringInput> & { is_active?: boolean }) =>
    api<{ recurring: RecurringInvoice }>(`/api/invoicing/recurring/${id}`, { method: 'PATCH', body: input }).then((r) => r.recurring),
  remove: (id: string) =>
    api<void>(`/api/invoicing/recurring/${id}`, { method: 'DELETE' }),
};

// ---- Settings ----
export interface TenantSettingsPatch {
  company_name?: string;
  company_logo_url?: string | null;
  company_accent_color?: string | null;
  company_address?: string | null;
  company_tagline?: string | null;
  company_phone?: string | null;
  company_email?: string | null;
  company_website?: string | null;
  default_invoice_due_days?: number;
  default_tax_rate?: number;
  invoice_number_prefix?: string;
  owner_notification_email?: string | null;
  owner_notification_mode?: 'instant' | 'hourly' | 'daily' | 'off';
  /** 036: opt-in automated client payment reminders for overdue invoices. */
  payment_reminders_enabled?: boolean;
}

export interface TenantSettingsSummary {
  invoice_default_terms: string | null;
}

export const settingsApi = {
  get: () =>
    api<{ settings: TenantSettingsSummary }>('/api/tenant/settings').then((r) => r.settings),
  update: (patch: TenantSettingsPatch) =>
    api<{ settings: any }>('/api/tenant/settings', { method: 'PATCH', body: patch }).then((r) => r.settings),
};

// ---- Tax profiles (036) ----
export interface TaxProfileInput {
  name?: string;
  rate_percent?: number;
  is_default?: boolean;
}

export const taxProfilesApi = {
  list: () =>
    api<{ tax_profiles: TaxProfile[] }>('/api/invoicing/tax-profiles').then((r) => r.tax_profiles),
  create: (input: TaxProfileInput) =>
    api<{ tax_profile: TaxProfile }>('/api/invoicing/tax-profiles', { method: 'POST', body: input }).then((r) => r.tax_profile),
  update: (id: string, input: TaxProfileInput) =>
    api<{ tax_profile: TaxProfile }>(`/api/invoicing/tax-profiles/${id}`, { method: 'PATCH', body: input }).then((r) => r.tax_profile),
  remove: (id: string) =>
    api<void>(`/api/invoicing/tax-profiles/${id}`, { method: 'DELETE' }),
};
